// What a card carries besides its mail (U6, Remember for me): the private note on its thread
// (shown first), whether it is pinned, and "Remind me the day before" when its email states a
// deadline. While the card is active it binds Shift+N (note), P (pin) and Shift+R (reminder).
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BellRing, Pin } from "lucide-react";
import { toast } from "sonner";
import type { DeadlineReminderView } from "@shared/api";
import { useCardMemory } from "@web/lib/rememberApi";
import { useCommand } from "@web/shell/commands";
import { Button } from "@web/ui/Button";
import { Kbd } from "@web/ui/Kbd";
import { remindBeforeDeadline, setCardPin } from "./memoryOps";
import { ThreadNote } from "./ThreadNote";
import { deadlineLine, remindLabel } from "./wording";

function DeadlineOffer({
  deadline,
  busy,
  onRemind,
}: {
  deadline: DeadlineReminderView;
  busy: boolean;
  onRemind: () => void;
}) {
  const offer = !deadline.taskId && !!deadline.remindAt;
  return (
    <div
      data-testid="deadline-reminder"
      className="flex flex-wrap items-center justify-between gap-2 text-sm text-ink-2"
    >
      <p className="min-w-0 flex-1 basis-60 break-words">{deadlineLine(deadline.quote)}</p>
      {offer ? (
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          aria-keyshortcuts="Shift+R"
          onPointerDown={(e) => e.preventDefault()}
          onClick={onRemind}
          title={remindLabel(deadline.remindWords)}
        >
          <BellRing aria-hidden className="size-4" />
          Remind me the day before
          <Kbd>⇧R</Kbd>
        </Button>
      ) : deadline.taskId ? (
        <span className="text-muted">Reminder set</span>
      ) : null}
    </div>
  );
}

function QuietButton({
  label,
  shortcut,
  onClick,
}: {
  label: string;
  shortcut: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 text-muted hover:text-ink"
      onPointerDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {label}
      <Kbd>{shortcut}</Kbd>
    </button>
  );
}

export function CardMemory({
  cardId,
  threadKey,
  active,
}: {
  cardId: string;
  threadKey: string | null;
  active: boolean;
}) {
  const queryClient = useQueryClient();
  const memory = useCardMemory(cardId);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const view = memory.data;
  const deadline = view?.deadline ?? null;

  const remind = async () => {
    if (!deadline?.remindAt || deadline.taskId || busy) return;
    setBusy(true);
    await remindBeforeDeadline(queryClient, cardId, {
      words: deadline.remindWords,
      remindAt: deadline.remindAt,
    });
    setBusy(false);
  };

  useCommand(
    "card.note",
    () => {
      if (threadKey) setEditing(true);
      else toast("This card has no email thread to keep a note on.");
    },
    active,
  );
  useCommand(
    "card.pin",
    () => void setCardPin(queryClient, cardId, !view?.pinned),
    active && !!view,
  );
  useCommand(
    "card.remindDeadline",
    () => {
      if (!deadline) toast("This card's email doesn't state a deadline.");
      else if (deadline.taskId) toast("There's already a reminder for this deadline.");
      else if (!deadline.remindAt) toast("That deadline is already here.");
      else void remind();
    },
    active && !!view,
  );

  if (!view) return null;
  return (
    <div className="flex flex-col gap-2" data-testid="card-memory">
      {threadKey ? (
        <ThreadNote
          threadKey={threadKey}
          note={view.note}
          editing={editing}
          onEditingChange={setEditing}
          shortcut="⇧N"
        />
      ) : null}
      {deadline ? (
        <DeadlineOffer deadline={deadline} busy={busy} onRemind={() => void remind()} />
      ) : null}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-2">
        {view.pinned ? (
          <span className="flex items-center gap-1.5">
            <Pin aria-hidden className="size-4" />
            Pinned · stays in view across rounds
          </span>
        ) : null}
        <QuietButton
          label={view.pinned ? "Unpin" : "Pin"}
          shortcut="P"
          onClick={() => void setCardPin(queryClient, cardId, !view.pinned)}
        />
        {threadKey && !view.note && !editing ? (
          <QuietButton label="Add a note" shortcut="⇧N" onClick={() => setEditing(true)} />
        ) : null}
      </div>
    </div>
  );
}
